import { SyntheticEvent, memo, useCallback, useEffect, useRef } from 'react'
import { SecureStateType } from '../../../constants/enums'
import { FLOATING_PREVIEW } from '../../../constants/floaters'
import { getModalState } from '../../../state/getState'
import {
    closeFloatingPreview,
    generateModalKey,
} from '../../../state/internal/modals'
import { clearAllPreviews } from '../../../state/modals/state.floater'
import { useFloatingPreviewSelector } from '../../Hooks/useModal'
import { useSecureState } from '../../Hooks/useSecureState'
import CameraButton from './Buttons/CameraButton'
import { setLargePreview } from './helpers'

interface LargeFloatingPreviewProps {
    id: number
    color?: string
    size?: number
    shadow?: boolean
    title?: string
}

const LargeFloatingPreview = memo((props: LargeFloatingPreviewProps) => {
    const { id, color, size, shadow, title } = props
    const { ageGateActive } = useSecureState(SecureStateType.AGE_VERIFICATION)
    const previewPinned = useFloatingPreviewSelector(id, 'close')
    const previewId = useFloatingPreviewSelector(id, 'id')

    const isPreviewPlaying = !!previewId
    const isPreviewPinned = previewPinned && isPreviewPlaying
    const previewIcon = useRef<HTMLDivElement>(null)
    // the sidebar opens previews under the same key, so only close one this button opened
    const openedPreview = useRef(false)

    // a grid shift can unmount the hovered button without a mouseleave; close the unpinned preview it opened
    useEffect(
        () => () => {
            const preview =
                getModalState().floater[generateModalKey(FLOATING_PREVIEW, id)]
            if (openedPreview.current && preview && !preview.close)
                closeFloatingPreview(id)
            openedPreview.current = false
        },
        [id],
    )

    const onLargePreviewClickCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            openedPreview.current = !isPreviewPinned
            setLargePreview(
                id,
                previewIcon.current,
                !isPreviewPinned,
                !isPreviewPinned,
            )
        },
        [id, isPreviewPinned],
    )
    const onLargePreviewMouseEnterCallback = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation()
            if (!isPreviewPinned) {
                clearAllPreviews()
                openedPreview.current = true
                setLargePreview(id, previewIcon.current)
            }
        },
        [id, isPreviewPinned],
    )

    const onLargePreviewMouseLeaveCallback = useCallback(() => {
        if (!isPreviewPinned) {
            openedPreview.current = false
            setLargePreview(id, null, false, false)
        }
    }, [id, isPreviewPinned])

    if (ageGateActive) {
        return null
    }

    return (
        <div ref={previewIcon}>
            <CameraButton
                onMouseEnter={onLargePreviewMouseEnterCallback}
                onMouseLeave={onLargePreviewMouseLeaveCallback}
                onClick={onLargePreviewClickCallback}
                color={color}
                size={size}
                filled={isPreviewPinned}
                shadow={shadow}
                title={title}
            />
        </div>
    )
})

export default LargeFloatingPreview
